/**
 * apps/web/src/lib/auth.ts — the auth store that the router context carries.
 *
 * Deliberately NOT a React context: `beforeLoad` guards run outside the React
 * tree, so the source of truth has to be a plain module store that the router
 * context points at. Components subscribe through `useAuth()`.
 *
 * Spec: 12-api-contract.md §3.1 (token model), 13-ui-screens.md §4.3
 * (landing routes), §18.0 (the capability matrix).
 *
 * `can()` is the ONLY gate. Roles are runtime data — an Admin can create one —
 * so `hasRole()` and `primaryRole()` exist for display and for the two places
 * that genuinely need to name a role, never for authorisation. The sider gates
 * on permissions for exactly this reason (see components/shell/nav-model.ts).
 */
import { useSyncExternalStore } from 'react';
import type { CompanyRefDto, CurrentUserDto, Permission, RoleCode } from '@cropmate/shared';
import {
  api,
  refreshAccessToken,
  setAccessToken,
  setCompanyId,
  getAccessToken,
} from '@/api/client';
import { resolveCompany } from '@/components/shell/company-model';

/* ══════════════════════════════ 1. The store ═════════════════════════════ */

export interface AuthSnapshot {
  user: CurrentUserDto | null;
  /** false until `bootstrap()` has settled — guards must not fire before then. */
  ready: boolean;
  /**
   * The company every request acts as.
   *
   * Client state, deliberately: with `X-Company-Id` the selection IS the request
   * scope, so there is no token to swap and no window in which a request composed
   * under one company lands under another. The server re-validates it against
   * `company_membership` every time, so this is a preference, never a privilege.
   *
   * `null` before `/auth/me` has been read, or when the account has no
   * membership at all.
   */
  companyId: string | null;
}

let snapshot: AuthSnapshot = { user: null, ready: false, companyId: null };
const listeners = new Set<() => void>();

function emit(next: AuthSnapshot): void {
  snapshot = next;
  for (const l of listeners) l();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/* ═══════════════════════ 2. Role display strings ═════════════════════════ */

/**
 * 13-ui-screens.md §4.2 — the top-bar label for the seven SYSTEM roles, verbatim.
 *
 * Deliberately a partial lookup keyed by plain string: `RoleCode` is `string`
 * now, so a custom role an Admin invents at runtime has no entry here and the
 * lookup returns `undefined`. Read it through `roleLabelFor()`, which supplies
 * a readable fallback — never index it and render the result raw.
 */
export const ROLE_LABEL: Readonly<Record<string, string>> = {
  CLERK: 'Clerk · Data entry',
  SUPERVISOR: 'Supervisor · Line I',
  HR: 'HR · Workforce',
  PAYROLL: 'Payroll',
  FINANCE: 'Finance',
  ADMIN: 'Administrator',
  AUDITOR: 'Auditor · Read only',
};

/**
 * A display label for ANY role code, system or custom.
 *
 * Custom codes are UPPER_SNAKE by contract, so `FIELD_LEAD` becomes
 * `Field Lead` — enough for a top-bar chip without a round-trip for the role's
 * stored `name`. Screens that have the `RoleDto` in hand should prefer
 * `role.name`; this is the fallback for the places that only hold a code.
 */
export function roleLabelFor(code: RoleCode): string {
  const known = ROLE_LABEL[code];
  if (known !== undefined) return known;
  return code
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ');
}

/** First letter of the first two name parts — the 28×28 avatar chip. */
export function initialsOf(displayName: string): string {
  const parts = displayName.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '?';
  const second = parts[1]?.[0] ?? '';
  return (first + second).toUpperCase();
}

/* ═══════════════════════════ 3. The public API ═══════════════════════════ */

export interface AuthStore {
  readonly user: CurrentUserDto | null;
  readonly ready: boolean;
  readonly isAuthenticated: boolean;
  /** Capability check. DOT strings — `can('job.read')`, never `job:read`. */
  can(permission: Permission): boolean;
  canAny(...permissions: Permission[]): boolean;
  hasRole(role: RoleCode): boolean;
  /** The primary role, used for the top-bar label. */
  primaryRole(): RoleCode | null;
  roleLabel(): string;
  /**
   * A1. `email` is the login identifier. It is trimmed here and lowercased
   * server-side, so what the caller passes need not already be normalised.
   */
  login(email: string, password: string, rememberMe: boolean): Promise<CurrentUserDto>;
  /**
   * A5. Changes the signed-in user's own password and re-reads A4, because the
   * server clears `mustChangePassword` as part of the write and the banner on
   * `/profile` reads that flag.
   *
   * The caller STAYS signed in: A5 revokes every OTHER session and carries this
   * one's `token_version` forward (auth.service.ts), so the `cm_rt` cookie
   * remains valid. The in-memory access token does NOT: it carries the old `tv`,
   * and since UAT ACC-025 `JwtStrategy` refuses a stale `tv` on every request.
   * So the token is re-minted through A2 straight away rather than leaving the
   * next request to take a 401 and recover.
   */
  changePassword(currentPassword: string, newPassword: string): Promise<void>;
  logout(): Promise<void>;
  /** Cold start: try the HttpOnly refresh cookie once, then load A4. */
  bootstrap(): Promise<void>;
  /** Re-read A4 after a role change so `can()` reflects the new set. */
  reload(): Promise<void>;
  clear(): void;

  /* ── companies ─────────────────────────────────────────────────────────── */

  /** Every company this account may act in. The SAME roles apply in all of them. */
  readonly companies: readonly CompanyRefDto[];
  /** The company in force, resolved against `companies` — see `company-model.ts`. */
  readonly currentCompany: CompanyRefDto | null;
  /**
   * Select a company.
   *
   * Synchronous and local: it updates the store and the header the next request
   * will carry. The CALLER owns what follows — clearing the query cache,
   * invalidating the router and navigating — because only the shell knows where
   * the user should land. See `_app.tsx`.
   *
   * `POST /auth/switch-company` is fired afterwards, not awaited: it audits the
   * move and remembers it as the default for the next sign-in. A failure there
   * costs the user nothing in this session.
   */
  setCompany(companyId: string): void;
}

/**
 * Precedence when a user holds several roles — highest privilege first.
 *
 * Only the seven SYSTEM roles can be ranked: a custom role's privilege is
 * whatever permissions an Admin gave it, which is not orderable. A user
 * holding no system role therefore falls through to their first role, and
 * `primaryRole()` still returns something the top bar can label. Nothing
 * except the label depends on this — every gate goes through `can()`.
 */
const ROLE_PRECEDENCE: readonly RoleCode[] = [
  'ADMIN',
  'FINANCE',
  'PAYROLL',
  'HR',
  'SUPERVISOR',
  'CLERK',
  'AUDITOR',
];

export const auth: AuthStore = {
  get user() {
    return snapshot.user;
  },
  get ready() {
    return snapshot.ready;
  },
  get isAuthenticated() {
    return snapshot.user !== null && getAccessToken() !== null;
  },

  can(permission) {
    return snapshot.user?.permissions.includes(permission) ?? false;
  },

  canAny(...permissions) {
    return permissions.some((p) => this.can(p));
  },

  hasRole(role) {
    return snapshot.user?.roles.includes(role) ?? false;
  },

  primaryRole() {
    const roles = snapshot.user?.roles;
    if (!roles || roles.length === 0) return null;
    return ROLE_PRECEDENCE.find((r) => roles.includes(r)) ?? roles[0] ?? null;
  },

  roleLabel() {
    const role = this.primaryRole();
    return role ? roleLabelFor(role) : '';
  },

  async login(email, password, rememberMe) {
    const res = await api.post<{ accessToken: string; user: CurrentUserDto }>(
      '/auth/login',
      { email, password, rememberMe },
      { skipAuth: true },
    );
    setAccessToken(res.accessToken);
    applyUser(res.user);
    return res.user;
  },

  async changePassword(currentPassword, newPassword) {
    /* 204, no body. A wrong `currentPassword` is a 401 `E_INVALID_CREDENTIALS`
     * that api/client.ts deliberately does NOT treat as a dead session. */
    await api.post<void>('/auth/change-password', { currentPassword, newPassword });
    await refreshAccessToken();
    await this.reload();
  },

  async logout() {
    try {
      await api.post<void>('/auth/logout');
    } catch {
      /* A3 is best-effort: the cookie is cleared server-side or it is not,
       * either way the SPA drops its in-memory token. */
    }
    this.clear();
  },

  async bootstrap() {
    if (getAccessToken() === null) {
      const refreshed = await refreshAccessToken();
      if (!refreshed) {
        emit({ user: null, ready: true, companyId: null });
        return;
      }
    }
    try {
      const user = await api.get<CurrentUserDto>('/auth/me');
      applyUser(user);
    } catch {
      setAccessToken(null);
      setCompanyId(null);
      emit({ user: null, ready: true, companyId: null });
    }
  },

  async reload() {
    const user = await api.get<CurrentUserDto>('/auth/me');
    applyUser(user);
  },

  clear() {
    setAccessToken(null);
    setCompanyId(null);
    emit({ user: null, ready: true, companyId: null });
  },

  get companies() {
    return snapshot.user?.companies ?? [];
  },

  get currentCompany() {
    return resolveCompany({
      companies: this.companies,
      currentCompanyId: snapshot.companyId,
    }).current;
  },

  setCompany(companyId) {
    setCompanyId(companyId);
    emit({ ...snapshot, companyId });
    // Audit and remember, but do NOT block the user on it. The switch has already
    // happened as far as this session is concerned; this records who moved where,
    // and which company to open next time they sign in.
    void api.post('/auth/switch-company', { companyId }).catch(() => undefined);
  },
};

/**
 * Adopt a freshly-read user, and keep the company selection consistent with it.
 *
 * Re-resolved on EVERY read, not just at login: an Admin can revoke a membership
 * or deactivate a company while the tab is open, and `/auth/me` is where the SPA
 * finds out. `resolveCompany` returns `null` for a selection that is no longer
 * valid, and this then falls back to the server's default rather than leaving the
 * client asserting a company it will be refused for.
 */
function applyUser(user: CurrentUserDto): void {
  const stillValid = resolveCompany({
    companies: user.companies,
    currentCompanyId: snapshot.companyId,
  }).current;
  const companyId = stillValid?.id ?? user.defaultCompanyId;
  setCompanyId(companyId);
  emit({ user, ready: true, companyId });
}

/* ═══════════════════════════ 4. React binding ════════════════════════════ */

export function useAuth(): AuthSnapshot &
  Pick<AuthStore, 'can' | 'canAny' | 'hasRole' | 'roleLabel' | 'primaryRole'> {
  const snap = useSyncExternalStore(
    subscribe,
    () => snapshot,
    () => snapshot,
  );
  return {
    user: snap.user,
    ready: snap.ready,
    companyId: snap.companyId,
    can: auth.can.bind(auth),
    canAny: auth.canAny.bind(auth),
    hasRole: auth.hasRole.bind(auth),
    roleLabel: auth.roleLabel.bind(auth),
    primaryRole: auth.primaryRole.bind(auth),
  };
}
